<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useSubscriptionStore } from '@organisms/ubiq-administration/store/subscriptions';
import {
  displayFrenchDate,
  getHumanDate,
} from '@tools/date';
import Panel from '../../../../_02_molecules/components/Panel.vue';
import { trans } from '../../../../_00_elements/translation';
import { currency } from '../../../../_00_elements/number-formatter';

const emit = defineEmits([ 'next-step' ]);

const subscriptionStore = useSubscriptionStore();
const isLoading = ref(false);

const { offices, invoice, clientInvoiceData } = storeToRefs(subscriptionStore);

const getFirstFacturationMonth = () => {
  const subscriptionBeginning = new Date(subscriptionStore.invoice.startDate);

  const currentMonth = new Date(subscriptionBeginning.getFullYear(), subscriptionBeginning.getMonth(), 1);

  return displayFrenchDate(currentMonth);
};

const onClickCreate = async () => {
  isLoading.value = true;
  await subscriptionStore.createSubscription();
  emit('next-step');
  isLoading.value = false;
  subscriptionStore.$reset();
};

const isStartingInCurrentMonth = computed(() => {
  const startDate = new Date(invoice.value.startDate);
  const now = new Date();
  return startDate.getFullYear() === now.getFullYear() && startDate.getMonth() === now.getMonth();
});
</script>

<template>
  <div class="stepper-subscription summary">
    <div class="stepper-subscription__title">
      {{ trans('gestion.subscription.step.6.title') }}
    </div>

    <div v-if="offices.length > 0" class="summary__block">
      <div class="summary__title">
        {{ trans('gestion.subscription.step.6.offices') }}
      </div>
      <div>
        <ul>
          <li v-for="office in offices" :key="office.id">
            {{ office.name }}
          </li>
        </ul>
      </div>
    </div>
    <div class="summary__block">
      <div class="summary__title">
        {{ trans('gestion.subscription.step.6.billing_info') }}
      </div>
      <div class="summary__content summary__content--columned">
        <div class="summary__content-row summary__content-row--firstcolumn">
          <div class="summary__content-title">
            {{ trans('gestion.subscription.step.6.organization') }}
          </div>
          <div class="summary__content-data">
            {{ clientInvoiceData.legalName }}<br>
            {{ clientInvoiceData.streetAndNumber }}<br>
            {{ clientInvoiceData.additionalInfo }}<br>
            {{ clientInvoiceData.zipCode }} {{ clientInvoiceData.city }}
          </div>
        </div>
        <div class="summary__content-row">
          <div class="summary__content-title">
            {{ trans('gestion.subscription.step.6.email') }}
          </div>
          <div class="summary__content-data">
            {{ clientInvoiceData.email }}
          </div>
        </div>
      </div>
    </div>
    <div class="summary__block">
      <div class="summary__title">
        {{ trans('gestion.subscription.step.6.payment_info') }}
      </div>
      <div class="summary__content summary__content--columned">
        <div class="summary__content-row summary__content-row--firstcolumn">
          <div class="summary__content-title">
            {{ trans('gestion.subscription.step.6.period') }}
          </div>
          <div class="summary__content-data">
            {{ getHumanDate(invoice.startDate) }} -
            {{
              invoice.endDate
                ? getHumanDate(invoice.endDate)
                : trans('gestion.subscription.step.6.unlimited')
            }}
          </div>
        </div>
        <div class="summary__content-row">
          <div class="summary__content-title">
            {{ trans('gestion.subscription.step.6.amount') }}
          </div>
          <div class="summary__content-data">
            {{ currency(invoice.amount / 100) }}
          </div>
        </div>
      </div>
    </div>
    <panel
      v-if="!isStartingInCurrentMonth"
      type="info"
      :title="trans('gestion.subscription.step.6.after_title')"
      :content-list="[
        trans('gestion.subscription.step.6.after_1', { formattedDate: getFirstFacturationMonth() }),
        trans('gestion.subscription.step.6.after_2'),
        trans('gestion.subscription.step.6.after_3')
      ]"
      icon="ph_lightbulb-fill_blue"
    />
    <panel
      v-if="isStartingInCurrentMonth"
      type="info"
      :title="trans('gestion.subscription.step.6.after_title')"
      :content-list="[
        trans('gestion.subscription.step.6.after_4'),
        trans('gestion.subscription.step.6.after_5'),
        trans('gestion.subscription.step.6.after_2'),
        trans('gestion.subscription.step.6.after_3')
      ]"
      icon="ph_lightbulb-fill_blue"
    />
    <button
      class="btn btn--primary stepper-subscription__button"
      :class="{ 'loading-btn': isLoading }"
      :disabled="isLoading"
      @click="onClickCreate"
    >
      {{ trans('gestion.subscription.step.6.create_payment') }}
    </button>
  </div>
</template>
